import React, {useContext, useState} from 'react';
import {
    Button,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Form, FormFeedback,
    FormGroup, FormText, Input, Label, Modal,
    ModalBody,
    ModalHeader
} from "reactstrap";
import Select from "react-select";
import Loader from "../../components/Loader";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
    editCustomer,
    getCountry,
    getCustomerShippingAddresses,
    getState
} from "../../api/api";
import {toast} from "react-toastify";
import handleUnauthorized401MutateError from "../../utils/handleUnauthorized401MutateError";
import AuthContext from "../../context/AuthContext";
import handleUnauthorized401QueryError from "../../utils/handleUnauthorized401QueryError";
import AddCustomerShippingAddress from "./AddCustomerShippingAddress";
import EditCustomerShippingAddress from "./EditCustomerShippingAddress";

function ActionButton(props) {
    const { refreshToken } = useContext(AuthContext);
    const [actionButtonDropdownOpen, setActionButtonDropdownOpen] = useState(false);
    const toggleActionButtonDropdown = () => setActionButtonDropdownOpen((prevState) => !prevState);
    const [customerUpdateToggle, setCustomerUpdateToggle] = useState(false);
    const toggleCustomerUpdateModal = () => setCustomerUpdateToggle(prevState => !prevState);
    const [customerName, setCustomerName] = useState(props.customer.name);
    const [customerImage, setCustomerImage] = useState('');
    const [customerEmail, setCustomerEmail] = useState(props.customer.email);
    const [customerPhone, setCustomerPhone] = useState(props.customer.phone);
    const [customerAddressLine1, setCustomerAddressLine1] = useState(props.customer.address_line1);
    const [customerAddressLine2, setCustomerAddressLine2] = useState(props.customer.address_line2);
    const [customerCountry, setCustomerCountry] = useState({value:props.customer.country, label:props.customer.country});
    const [customerState, setCustomerState] = useState({value:props.customer.state, label:props.customer.state});
    // const [customerCity, setCustomerCity] = useState({value:props.customer.city, label:props.customer.city});
    const [customerCity, setCustomerCity] = useState(props.customer.city);
    const [customerPincode, setCustomerPincode] = useState(props.customer.pincode);
    const [customerGSTIN, setCustomerGSTIN] = useState(props.customer.gstin);
    const [customerGSTINError, setCustomerGSTINError] = useState("");
    const [customerIsSEZ, setCustomerIsSEZ] = useState(props.customer.is_sez || false);
    const [customerSEZType, setCustomerSEZType] = useState(props.customer.sez_type || "");
    const [customerIsRCM, setCustomerIsRCM] = useState(props.customer.is_reverse_charge || false);

    const [countryOptions, setCountryOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    // const [cityOptions, setCityOptions] = useState([]);

    const [advancedOptions, setAdvancedOptions] = useState(false);


    // const [selectedShippingAddress, setSelectedShippingAddress] = useState(null);
    const [shippingAddressModalOpen, setShippingAddressModalOpen] = useState(false);
    const toggleShippingAddressModal = () => setShippingAddressModalOpen((prevState) => !prevState);

    const [addShippingModalOpen, setAddShippingModalOpen] = useState(false);
    const [editShippingModalOpen, setEditShippingModalOpen] = useState(false);
    const [editingShippingAddress, setEditingShippingAddress] = useState(null);
    const toggleAddShippingModal = () =>
        setAddShippingModalOpen(prev => !prev);

    const toggleEditShippingModal = () =>
        setEditShippingModalOpen(prev => !prev);


    const queryClient = useQueryClient();
    const page = 1;

    useQuery(
        ['country'],
        () => getCountry(1, '', '', '', '', 100),
        {
            enabled: customerUpdateToggle,
            // staleTime: Infinity,
            onSuccess: (data) => {
                const options = data.results.map((country) => ({
                    value: country.name, label: country.name
                }));
                setCountryOptions(options);
            },
            onError: (error) => {
                handleUnauthorized401QueryError(error, refreshToken);
                if (error.response.status !== 401) {
                    toast.error(`An error occurred! ${error.response.data.detail}`);
                }
            }
        }
    );

    useQuery(
        ['state', customerCountry?.value],
        () => getState(1, '', '', customerCountry?.value, '', '', 2000),
        {
            enabled: !!customerCountry,
            // staleTime: Infinity,
            onSuccess: (data) => {
                const options = data.results.map(state => ({
                    value: state.name,
                    label: state.name
                }));
                setStateOptions(options);
                // setCustomerState(null);
            },
            onError: (error) => {
                handleUnauthorized401QueryError(error, refreshToken);
                if (error.response.status !== 401) {
                    toast.error(`An error occurred! ${error.response.data.detail}`);
                }
            },
        }
    );

    const {
        data: shippingAddressData,
        isLoading: isShippingAddressLoading,
        refetch: refetchShippingAddress
    } = useQuery({
        queryKey: ['shippingAddress', customerEmail],
        queryFn: () =>
            getCustomerShippingAddresses(page, customerEmail),
        enabled: !!customerEmail,
        retry: false,
        onError: (err) => {
            handleUnauthorized401QueryError(err, refetchShippingAddress);
            if (err?.response?.status !== 401) {
                toast.error(err?.response?.data?.detail || 'Failed to load shipping addresses');
            }
        }
    });


    // useQuery(
    //     ['city', customerState?.value],
    //     () => getCity(1, '', '', customerState?.value, '', '', 2000),
    //     {
    //         enabled: !!customerState,
    //         // staleTime: Infinity,
    //         onSuccess: (data) => {
    //             const options = data.results.map(city => ({
    //                 value: city.name,
    //                 label: city.name
    //             }));
    //             setCityOptions(options);
    //             setCustomerCity(null);
    //         },
    //         onError: (error) => {
    //             handleUnauthorized401QueryError(error, refreshToken);
    //             if (error.response.status !== 401) {
    //                 toast.error(`An error occurred! ${error.response.data.detail}`);
    //             }
    //         }
    //     }
    // );

    const editCustomerMutation = useMutation({
        mutationFn: editCustomer,
        onSuccess: () => {
            toggleCustomerUpdateModal();
            queryClient.invalidateQueries(['customers']);
            queryClient.invalidateQueries(['cart']);
            toast.success('Customer updated!');
        },
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, editCustomerMutation, variables);
            if (err.response.status !== 401) {
                toast.error(`An error occurred! ${err?.response?.data?.detail}`);
            }
        }
    });

    const handleEditCustomer = async (customer, e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append("name", customerName);
        formData.append("email", customerEmail);
        formData.append("phone", customerPhone);
        formData.append("address_line1", customerAddressLine1);
        formData.append("address_line2", customerAddressLine2);
        formData.append("country", customerCountry?.value || '');
        formData.append("state", customerState?.value || '');
        // formData.append("city", customerCity?.value || '');
        formData.append("city", customerCity);
        formData.append("pincode", customerPincode);
        formData.append("gstin", customerGSTIN);
        formData.append("is_sez", customerIsSEZ);
        formData.append("sez_type", customerIsSEZ ? customerSEZType : "");
        formData.append("is_reverse_charge", customerIsRCM);
        // formData.append("credit_limit", customerCreditLimit);
        // formData.append("is_vip", customerIsVIP);

        if (customerImage) formData.append("image", customerImage);

        editCustomerMutation.mutate({
            formData: formData,
            customerId: customer.id
        });
    };


    return (
        <>
            <td className="text-center">
                {/*<div className="btn-group dropdown">*/}
                {/*    <a href="javascript: void(0);" className="dropdown-toggle arrow-none btn btn-light btn-sm"*/}
                {/*       data-toggle="dropdown" aria-expanded="false"><i className="mdi mdi-dots-horizontal"></i></a>*/}
                {/*    <div className="dropdown-menu dropdown-menu-right">*/}
                {/*        <a className="dropdown-item" href="#"><i*/}
                {/*            className="mdi mdi-pencil mr-2 text-muted font-18 vertical-middle"></i>Edit Ticket</a>*/}
                {/*    </div>*/}
                {/*</div>*/}
                <Dropdown
                    nav
                    isOpen={actionButtonDropdownOpen}
                    toggle={toggleActionButtonDropdown}
                    className="btn-group"
                >
                    <DropdownToggle
                        caret
                        className="arrow-none btn btn-light btn-sm"
                    >
                        <i className="mdi mdi-dots-horizontal"/>
                    </DropdownToggle>
                    <DropdownMenu className="dropdown-menu-right profile-dropdown">
                        <DropdownItem
                            onClick={toggleCustomerUpdateModal}
                        >
                            <i className="mdi mdi-pencil mr-2 text-muted font-18 vertical-middle"/>
                            <span>Edit Customer</span>
                        </DropdownItem>

                        <DropdownItem
                            onClick={toggleShippingAddressModal}
                        >
                            <i className="mdi mdi-pencil mr-2 text-muted font-18 vertical-middle"/>
                            <span>Add/Edit Shipping Address</span>
                        </DropdownItem>
                    </DropdownMenu>
                </Dropdown>


                <Modal
                    isOpen={customerUpdateToggle}
                    toggle={toggleCustomerUpdateModal}
                    className=""
                >
                    <ModalHeader toggle={toggleCustomerUpdateModal} close={
                        <button className="close" onClick={toggleCustomerUpdateModal}>
                            &times;
                        </button>
                    }>
                        Update Customer
                    </ModalHeader>
                    <ModalBody>
                        <Form onSubmit={(e) => handleEditCustomer(props.customer, e)}>
                            <FormGroup>
                                <Label for="customer-name">Name<span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    name="customer-name"
                                    id="customer-name"
                                    placeholder="Customer Name"
                                    defaultValue={props.customer.name}
                                    onChange={(e) =>
                                        setCustomerName(e.target.value)
                                    }
                                    invalid={
                                        editCustomerMutation.isError &&
                                        editCustomerMutation.error.response.data.name
                                    }
                                />
                                {editCustomerMutation.isError && editCustomerMutation.error.response.data.name && (
                                    <FormFeedback invalid>
                                        {editCustomerMutation.error.response.data.name}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                            <FormGroup>
                                <Label for="product-group-price">Email<span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    name="customer-email"
                                    id="customer-email"
                                    placeholder="Customer Email"
                                    defaultValue={props.customer.email}
                                    onChange={(e) =>
                                        setCustomerEmail(e.target.value)
                                    }
                                    invalid={
                                        editCustomerMutation.isError &&
                                        editCustomerMutation.error.response.data.email
                                    }
                                />
                                {editCustomerMutation.isError && editCustomerMutation.error.response.data.email && (
                                    <FormFeedback invalid>
                                        {editCustomerMutation.error.response.data.email}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                            <FormGroup>
                                <Label for="customer-phone">Phone</Label>
                                <Input
                                    type="text"
                                    name="customer-phone"
                                    id="customer-phone"
                                    placeholder="Customer Phone"
                                    defaultValue={props.customer.phone}
                                    onChange={(e) =>
                                        setCustomerPhone(e.target.value)
                                    }
                                    invalid={
                                        editCustomerMutation.isError &&
                                        editCustomerMutation.error.response.data.phone
                                    }
                                />
                                {editCustomerMutation.isError && editCustomerMutation.error.response.data.phone && (
                                    <FormFeedback invalid>
                                        {editCustomerMutation.error.response.data.phone}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                            {/*{user?.membership !== "BASIC" && (*/}
                            {/*<FormGroup className="form-group mb-3">*/}
                            {/*    <div className="custom-control custom-checkbox">*/}
                            {/*        <Input*/}
                            {/*            type="checkbox"*/}
                            {/*            className="custom-control-input"*/}
                            {/*            id="checkbox-VIP"*/}
                            {/*            checked={customerIsVIP}*/}
                            {/*            onChange={() => setCustomerIsVIP(!customerIsVIP)}*/}
                            {/*            invalid={*/}
                            {/*                editCustomerMutation.isError &&*/}
                            {/*                editCustomerMutation.error.response.data.is_vip*/}
                            {/*            }*/}
                            {/*        />*/}
                            {/*        <label*/}
                            {/*            className="custom-control-label"*/}
                            {/*            for="checkbox-VIP"*/}
                            {/*        >*/}
                            {/*            Mark As VIP*/}
                            {/*        </label>*/}
                            {/*    </div>*/}
                            {/*    {editCustomerMutation.isError && editCustomerMutation.error.response.data.is_vip && (*/}
                            {/*        <FormFeedback invalid>*/}
                            {/*            {editCustomerMutation.error.response.data.is_vip}*/}
                            {/*        </FormFeedback>*/}
                            {/*    )}*/}
                            {/*</FormGroup>*/}
                            {/*)}*/}
                            {/*{customerIsVIP && user?.membership !== "BASIC" && (*/}
                            {/*    <FormGroup>*/}
                            {/*    <Label for="credit limit">Credit Limit</Label>*/}
                            {/*    <Input*/}
                            {/*        type="text"*/}
                            {/*        name="credit limit"*/}
                            {/*        id="credit limit"*/}
                            {/*        placeholder="Credit Limit"*/}
                            {/*        defaultValue={props.customer.credit_limit}*/}
                            {/*        onChange={(e) =>*/}
                            {/*            setCustomerCreditLimit(e.target.value)*/}
                            {/*        }*/}
                            {/*        invalid={*/}
                            {/*            editCustomerMutation.isError &&*/}
                            {/*            editCustomerMutation.error.response.data.credit_limit*/}
                            {/*        }*/}
                            {/*    />*/}
                            {/*    {editCustomerMutation.isError && editCustomerMutation.error.response.data.credit_limit && (*/}
                            {/*        <FormFeedback invalid>*/}
                            {/*            {editCustomerMutation.error.response.data.credit_limit}*/}
                            {/*        </FormFeedback>*/}
                            {/*    )}*/}
                            {/*</FormGroup>*/}
                            {/*)}*/}
                            <FormGroup className="form-group mb-3">
                                <div className="custom-control custom-checkbox">
                                    <Input
                                        type="checkbox"
                                        className="custom-control-input"
                                        id="checkbox-advanced-options"
                                        checked={advancedOptions}
                                        onChange={() => setAdvancedOptions(!advancedOptions)}
                                    />
                                    <Label
                                        className="custom-control-label"
                                        for="checkbox-advanced-options"                                    >
                                        Advanced Options
                                    </Label>
                                </div>
                            </FormGroup>
                            {advancedOptions&& (
                                <FormGroup>
                                    <Label for="customer-addressline1">Address Line 1</Label>
                                    <Input
                                        type="text"
                                        name="customer-addressline1"
                                        id="customer-addressline1"
                                        placeholder="Customer address line1"
                                        defaultValue={props.customer.address_line1}
                                        onChange={(e) => setCustomerAddressLine1(e.target.value)}
                                        invalid={
                                            editCustomerMutation.isError &&
                                            editCustomerMutation.error.response.data.address_line1
                                        }
                                    />
                                    {editCustomerMutation.isError && editCustomerMutation.error.response.data.address_line1 && (
                                        <FormFeedback invalid>
                                            {editCustomerMutation.error.response.data.address_line1}
                                        </FormFeedback>
                                    )}
                                </FormGroup>
                            )}
                            {advancedOptions&& (
                                <FormGroup>
                                    <Label for="customer-addressline2">Address Line 2</Label>
                                    <Input
                                        type="text"
                                        name="customer-addressline2"
                                        id="customer-addressline2"
                                        placeholder="Customer address line2"
                                        defaultValue={props.customer.address_line2}
                                        onChange={(e) => setCustomerAddressLine2(e.target.value)}
                                        invalid={
                                            editCustomerMutation.isError &&
                                            editCustomerMutation.error.response.data.address_line2
                                        }
                                    />
                                    {editCustomerMutation.isError && editCustomerMutation.error.response.data.address_line2 && (
                                        <FormFeedback invalid>
                                            {editCustomerMutation.error.response.data.address_line2}
                                        </FormFeedback>
                                    )}
                                </FormGroup>
                            )}
                            {advancedOptions&& (
                                <FormGroup>
                                    <Label for="country">
                                        Country
                                    </Label>
                                    <Select
                                        options={countryOptions}
                                        placeholder = "Select country"
                                        onChange={(option) => {
                                            console.log("optionnnnnnnnnnnn", option)
                                            setCustomerCountry(option);
                                            setCustomerState(null);
                                            setCustomerCity(null);
                                        }}
                                        value={customerCountry}
                                        // defaultValue={{label: customerCountry, value: customerCountry}}
                                        styles={{
                                            control: (baseStyles) => (
                                                {
                                                    ...baseStyles,
                                                    borderColor: "#495561",
                                                    backgroundColor: "#414d59",
                                                    color: "white",
                                                    padding: "0.45 0.9",
                                                    border: 'none',
                                                    boxShadow: 'none'
                                                }
                                            ),
                                            menu: (baseStyles) => (
                                                {
                                                    ...baseStyles,
                                                    backgroundColor: "#414d59",
                                                    color: "#a8b5c3"
                                                }
                                            ),
                                            input: (baseStyles) => (
                                                {
                                                    ...baseStyles,
                                                    color: "white"
                                                }
                                            ),
                                            singleValue: (baseStyles) => ({
                                                ...baseStyles,
                                                color: "white"
                                            }),
                                        }}
                                        invalid={
                                            editCustomerMutation.isError &&
                                            editCustomerMutation.error.response.data.country
                                        }
                                    />
                                    {editCustomerMutation.isError && editCustomerMutation.error.response.data.country && (
                                        <FormFeedback invalid>
                                            {editCustomerMutation.error.response.data.country}
                                        </FormFeedback>
                                    )}
                                </FormGroup>
                            )}
                            {advancedOptions&& (
                                <FormGroup>
                                    <Label for="state">
                                        State
                                    </Label>
                                    <Select
                                        options={stateOptions}
                                        placeholder = "Select state"
                                        onChange={(option) => {
                                            setCustomerState(option);
                                            setCustomerCity(null);
                                        }}
                                        value={customerState}
                                        // defaultValue={{label: customerState, value: customerState}}
                                        isDisabled={!customerCountry}
                                        styles={{
                                            control: (baseStyles) => (
                                                {
                                                    ...baseStyles,
                                                    borderColor: "#495561",
                                                    backgroundColor: "#414d59",
                                                    color: "white",
                                                    padding: "0.45 0.9",
                                                    border: 'none',
                                                    boxShadow: 'none'
                                                }
                                            ),
                                            menu: (baseStyles) => (
                                                {
                                                    ...baseStyles,
                                                    backgroundColor: "#414d59",
                                                    color: "#a8b5c3"
                                                }
                                            ),
                                            input: (baseStyles) => (
                                                {
                                                    ...baseStyles,
                                                    color: "white"
                                                }
                                            ),
                                            singleValue: (baseStyles) => ({
                                                ...baseStyles,
                                                color: "white"
                                            }),
                                        }}
                                        invalid={
                                            editCustomerMutation.isError &&
                                            editCustomerMutation.error.response.data.state
                                        }
                                    />
                                    {editCustomerMutation.isError && editCustomerMutation.error.response.data.state && (
                                        <FormFeedback invalid>
                                            {editCustomerMutation.error.response.data.state}
                                        </FormFeedback>
                                    )}
                                </FormGroup>
                            )}
                            {/*{advancedOptions&& (*/}
                            {/*    <FormGroup>*/}
                            {/*        <Label for="city">*/}
                            {/*            City*/}
                            {/*        </Label>*/}
                            {/*        <Select*/}
                            {/*            options={cityOptions}*/}
                            {/*            placeholder = "Select city"*/}
                            {/*            onChange={(option) => setCustomerCity(option)}*/}
                            {/*            value={customerCity}*/}
                            {/*            // defaultValue={{label: props.customer.city, value: props.customer.city}}*/}
                            {/*            isDisabled={!customerState}*/}
                            {/*            styles={{*/}
                            {/*                control: (baseStyles) => (*/}
                            {/*                    {*/}
                            {/*                        ...baseStyles,*/}
                            {/*                        borderColor: "#495561",*/}
                            {/*                        backgroundColor: "#414d59",*/}
                            {/*                        color: "white",*/}
                            {/*                        padding: "0.45 0.9",*/}
                            {/*                        border: 'none',*/}
                            {/*                        boxShadow: 'none'*/}
                            {/*                    }*/}
                            {/*                ),*/}
                            {/*                menu: (baseStyles) => (*/}
                            {/*                    {*/}
                            {/*                        ...baseStyles,*/}
                            {/*                        backgroundColor: "#414d59",*/}
                            {/*                        color: "#a8b5c3"*/}
                            {/*                    }*/}
                            {/*                ),*/}
                            {/*                input: (baseStyles) => (*/}
                            {/*                    {*/}
                            {/*                        ...baseStyles,*/}
                            {/*                        color: "white"*/}
                            {/*                    }*/}
                            {/*                ),*/}
                            {/*                singleValue: (baseStyles) => ({*/}
                            {/*                    ...baseStyles,*/}
                            {/*                    color: "white"*/}
                            {/*                }),*/}
                            {/*            }}*/}
                            {/*            invalid={*/}
                            {/*                editCustomerMutation.isError &&*/}
                            {/*                editCustomerMutation.error.response.data.city*/}
                            {/*            }*/}
                            {/*        />*/}
                            {/*        {editCustomerMutation.isError && editCustomerMutation.error.response.data.city && (*/}
                            {/*            <FormFeedback invalid>*/}
                            {/*                {editCustomerMutation.error.response.data.city}*/}
                            {/*            </FormFeedback>*/}
                            {/*        )}*/}
                            {/*    </FormGroup>*/}
                            {/*)}*/}
                            {advancedOptions&& (
                                <FormGroup>
                                    <Label for="customer-pincode"> City </Label>
                                    <Input
                                        type="text"
                                        name="customer-city"
                                        id="customer-city"
                                        placeholder="Customer City"
                                        defaultValue={props.customer.city}
                                        onChange={(e) => setCustomerCity(e.target.value)}
                                        invalid={
                                            editCustomerMutation.isError &&
                                            editCustomerMutation.error.response.data.city
                                        }
                                    />
                                    {editCustomerMutation.isError && editCustomerMutation.error.response.data.city && (
                                        <FormFeedback invalid>
                                            {editCustomerMutation.error.response.data.city}
                                        </FormFeedback>
                                    )}
                                </FormGroup>
                            )}
                            {advancedOptions&& (
                                <FormGroup>
                                    <Label for="customer-pincode"> Pincode </Label>
                                    <Input
                                        type="text"
                                        name="customer-pincode"
                                        id="customer-pincode"
                                        placeholder="Customer pincode"
                                        defaultValue={props.customer.pincode}
                                        onChange={(e) => setCustomerPincode(e.target.value)}
                                        invalid={
                                            editCustomerMutation.isError &&
                                            editCustomerMutation.error.response.data.pincode
                                        }
                                    />
                                    {editCustomerMutation.isError && editCustomerMutation.error.response.data.pincode && (
                                        <FormFeedback invalid>
                                            {editCustomerMutation.error.response.data.pincode}
                                        </FormFeedback>
                                    )}
                                </FormGroup>
                            )}
                            {advancedOptions&& (
                                <FormGroup>
                                    <Label for="customer-pincode"> GSTIN </Label>
                                    <Input
                                        type="text"
                                        name="customer-gstin"
                                        id="customer-gstin"
                                        placeholder="Customer GSTIN"
                                        defaultValue={props.customer.gstin}
                                        onChange={(e) => {
                                            const val = e.target.value.toUpperCase();
                                            setCustomerGSTIN(val);
                                            setCustomerGSTINError(val && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(val)
                                                ? "Enter a valid GSTIN."
                                                : "");
                                        }}
                                        invalid={
                                            !!customerGSTINError ||
                                            (editCustomerMutation.isError &&
                                            editCustomerMutation.error.response.data.gstin)
                                        }
                                    />
                                    {customerGSTINError && (
                                        <FormFeedback invalid>{customerGSTINError}</FormFeedback>
                                    )}
                                    {!customerGSTINError && editCustomerMutation.isError && editCustomerMutation.error.response.data.gstin && (
                                        <FormFeedback invalid>
                                            {editCustomerMutation.error.response.data.gstin}
                                        </FormFeedback>
                                    )}
                                </FormGroup>
                            )}
                            {advancedOptions && (
                                <FormGroup check className="mb-2">
                                    <Input
                                        type="checkbox"
                                        id="customer-is-sez"
                                        checked={customerIsSEZ}
                                        onChange={() => { setCustomerIsSEZ(!customerIsSEZ); setCustomerSEZType(""); }}
                                    />
                                    <Label check for="customer-is-sez"> SEZ Customer</Label>
                                </FormGroup>
                            )}
                            {advancedOptions && customerIsSEZ && (
                                <FormGroup>
                                    <Label for="customer-sez-type">SEZ Type</Label>
                                    <Select
                                        inputId="customer-sez-type"
                                        options={[
                                            { value: "SEZ_UNIT", label: "SEZ Unit" },
                                            { value: "SEZ_DEVELOPER", label: "SEZ Developer" },
                                        ]}
                                        placeholder="Select SEZ Type"
                                        value={customerSEZType ? { value: customerSEZType, label: customerSEZType === "SEZ_UNIT" ? "SEZ Unit" : "SEZ Developer" } : null}
                                        onChange={(option) => setCustomerSEZType(option ? option.value : "")}
                                        styles={{
                                            control: (baseStyles) => ({
                                                ...baseStyles,
                                                borderColor: "#495561",
                                                backgroundColor: "#414d59",
                                                color: "white",
                                                padding: "0.45 0.9",
                                                border: 'none',
                                                boxShadow: 'none',
                                            }),
                                            menu: (baseStyles) => ({
                                                ...baseStyles,
                                                backgroundColor: "#414d59",
                                                color: "#a8b5c3",
                                            }),
                                            input: (baseStyles) => ({
                                                ...baseStyles,
                                                color: "white",
                                            }),
                                            singleValue: (baseStyles) => ({
                                                ...baseStyles,
                                                color: "white",
                                            }),
                                        }}
                                    />
                                    {editCustomerMutation.isError && editCustomerMutation.error?.response?.data?.sez_type && (
                                        <div className="text-danger small mt-1">
                                            {editCustomerMutation.error.response.data.sez_type}
                                        </div>
                                    )}
                                </FormGroup>
                            )}
                            {advancedOptions && (
                                <FormGroup check className="mb-2">
                                    <Input
                                        type="checkbox"
                                        id="customer-is-rcm"
                                        checked={customerIsRCM}
                                        onChange={() => setCustomerIsRCM(!customerIsRCM)}
                                    />
                                    <Label check for="customer-is-rcm"> Reverse Charge Applicable</Label>
                                    <div className="small text-muted mt-1">
                                        Enable if GST on this customer's purchases is paid directly by them (Section 9(3)/(4)).
                                    </div>
                                </FormGroup>
                            )}
                            <FormGroup>
                                <Label for="customer-image">Image</Label>
                                <Input
                                    type="file"
                                    name="customer-image"
                                    id="customer-image"
                                    // value={props.customer.image}
                                    onChange={(e) =>
                                        setCustomerImage(e.target.files[0])
                                    }
                                    invalid={
                                        editCustomerMutation.isError &&
                                        editCustomerMutation.error.response.data.image
                                    }
                                />
                                <FormText color="muted">
                                    {props.customer.image}
                                </FormText>
                                {editCustomerMutation.isError && editCustomerMutation.error.response.data.image && (
                                    <FormFeedback invalid>
                                        {editCustomerMutation.error.response.data.image}
                                    </FormFeedback>
                                )}
                            </FormGroup>

                            <Button
                                color="#4d5bed"
                                className="bx-btn-primary"
                                block
                                disabled={editCustomerMutation.isLoading}
                            >
                                {editCustomerMutation.isLoading? <><Loader size={'small'}/> Updating Product Group...</>: `Update Customer`}

                            </Button>
                        </Form>
                    </ModalBody>
                </Modal>

                <Modal
                    className="modal-xl"
                    isOpen={shippingAddressModalOpen}
                    toggle={toggleShippingAddressModal}
                >
                    <ModalHeader toggle={toggleShippingAddressModal} close={
                        <button className="close" onClick={toggleShippingAddressModal}>
                            &times;
                        </button>
                    }>
                        Shipping Address
                    </ModalHeader>

                    <ModalBody>

                        <Button
                            color="#4d5bed"
                            className="bx-btn-primary"
                            onClick={toggleAddShippingModal}
                        >
                            + Add New Shipping Address
                        </Button>

                        {isShippingAddressLoading ? (
                            <p>Loading addresses...</p>
                        ) : (
                            <div className="row">

                                {/* Customer shipping addresses */}
                                {shippingAddressData?.results?.length ? (
                                    shippingAddressData.results.map((address) => (
                                        // <div className="col-md-6 mt-2" key={address.id}>
                                        //     <div className="border p-3 rounded">
                                        //         <div className="form-check font-16">
                                        // {/*            <Input*/}
                                        // {/*                type="radio"*/}
                                        // {/*                name="shippingAddress"*/}
                                        // {/*                className="form-check-input"*/}
                                        // {/*                checked={selectedShippingAddress === address.id}*/}
                                        // {/*                onChange={() =>*/}
                                        // {/*                    setSelectedShippingAddress(address.id)*/}
                                        // {/*                }*/}
                                        // {/*            />*/}
                                        // {/*            <label className="form-check-label fw-semibold">*/}
                                        // {/*                {address.label}*/}
                                        // {/*                {address.is_primary && (*/}
                                        // {/*                    <span className="badge bg-success ms-2">*/}
                                        // {/*    Primary*/}
                                        // {/*</span>*/}
                                        // {/*                )}*/}
                                        // {/*            </label>*/}
                                        // {/*            <i*/}
                                        // {/*                className="mdi mdi-pencil-outline cursor-pointer text-muted"*/}
                                        // {/*                onClick={() => {*/}
                                        // {/*                    setEditingShippingAddress(address);*/}
                                        // {/*                    setEditShippingModalOpen(true);*/}
                                        // {/*                }}*/}
                                        // {/*            />*/}
                                        //             <div>
                                        //
                                        //                 <div className="fw-semibold font-16">
                                        //                     <i
                                        //                         className="mdi mdi-square-edit-outline text-muted font-20 cursor-pointer"
                                        //                         title="Edit shipping address"
                                        //                         onClick={() => {
                                        //                             setEditingShippingAddress(address);
                                        //                             setEditShippingModalOpen(true);
                                        //                         }}
                                        //                     />
                                        //                     {address.label}
                                        //                     {address.is_primary && (
                                        //                         <span className="badge bg-success ms-2">Primary</span>
                                        //                     )}
                                        //                 </div>
                                        //             </div>
                                        //         </div>
                                        //
                                        //         <p className="mt-2 mb-1">
                                        //             {address.address_line1}
                                        //             {address.address_line2 && `, ${address.address_line2}`}
                                        //             {address.address_line3 && `, ${address.address_line3}`}
                                        //         </p>
                                        //
                                        //         <p className="mb-1">
                                        //             {address.city}, {address.state?.name} - {address.pincode}
                                        //         </p>
                                        //
                                        //         {address.phone_number && (
                                        //             <p className="mb-0">
                                        //                 <strong>Phone:</strong> {address.phone_number}
                                        //             </p>
                                        //         )}
                                        //     </div>
                                        // </div>
                                        <div className="col-md-6 mt-3" key={address.id}>
                                            <div className="border p-3 rounded">

                                                {/* Header row */}
                                                <div className="d-flex justify-content-between align-items-start">
                                                    {/*<div className="form-check form-check-inline font-16">*/}
                                                    {/*        {address.label}*/}
                                                    {/*</div>*/}
                                                    <h5>
                                                        {address.label}
                                                        {address.is_primary && (
                                                            <span className="badge bg-success ml-1">Primary</span>
                                                        )}
                                                    </h5>

                                                    <div className="float-end"
                                                         style={{ cursor: "pointer" }}
                                                    >
                                                        <i
                                                            className="mdi mdi-square-edit-outline text-muted font-20 cursor-pointer"
                                                            title="Edit shipping address"
                                                            onClick={() => {
                                                                setEditingShippingAddress(address);
                                                                setEditShippingModalOpen(true);
                                                            }}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Name */}
                                                {/*<h5 className="mt-3 mb-2">*/}
                                                {/*    {props.customer.name}*/}
                                                {/*</h5>*/}

                                                {/* Address */}
                                                {/*<p className="mb-1">*/}
                                                {/*    /!*<span className="fw-semibold me-2">Address:</span>*!/*/}
                                                {/*    {address.address_line1}*/}
                                                {/*    {address.address_line2 && `, ${address.address_line2}`}*/}
                                                {/*    {address.address_line3 && `, ${address.address_line3}`}*/}
                                                {/*    , {address.city}, {address.state.name} - {address.pincode}*/}
                                                {/*</p>*/}
                                                <p className="mb-1">
                                                    {address.address_line1}
                                                    {address.address_line2 && `, ${address.address_line2}`}
                                                    {address.address_line3 && `, ${address.address_line3}`}
                                                    , {address.city}
                                                    {address.state && `, ${address.state?.name || address.state}`}
                                                    {address.country && `, ${address.country?.name || address.country}`}
                                                    {" - "}
                                                    {address.pincode}
                                                </p>


                                                {/* Phone */}
                                                {address.phone_number && (
                                                    <p className="mb-0">
                                                        <span className="fw-semibold me-2">Phone:</span>
                                                        {address.phone_number}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                    ))
                                ) : (
                                    <div className="col-12">
                                        <p className="text-muted mt-2">
                                            No shipping addresses found.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </ModalBody>


                    {/*<ModalFooter className="d-flex justify-content-end">*/}
                    {/*    /!*<Button*!/*/}
                    {/*    /!*    color="primary"*!/*/}
                    {/*    /!*    disabled={!selectedShippingAddress}*!/*/}
                    {/*    /!*    onClick={() => {*!/*/}
                    {/*    /!*        // later: PATCH cart with selectedShippingAddress*!/*/}
                    {/*    /!*        toggleShippingAddressModal();*!/*/}
                    {/*    /!*    }}*!/*/}
                    {/*    /!*>*!/*/}
                    {/*    /!*    Use this address*!/*/}
                    {/*    /!*</Button>*!/*/}

                    {/*    <Button*/}
                    {/*        color="primary"*/}
                    {/*        disabled={updateShippingAddressMutation.isLoading}*/}
                    {/*        onClick={() => {*/}
                    {/*            const cartId = cartData?.results?.[0]?.id;*/}

                    {/*            updateShippingAddressMutation.mutate({*/}
                    {/*                cartId,*/}
                    {/*                shippingAddress: selectedShippingAddress*/}
                    {/*            });*/}
                    {/*        }}*/}
                    {/*    >*/}
                    {/*        {updateShippingAddressMutation.isLoading*/}
                    {/*            ? <><Loader size="small" /> Saving...</>*/}
                    {/*            : 'Use this address'}*/}
                    {/*    </Button>*/}
                    {/*</ModalFooter>*/}
                </Modal>

                {/*<EditCustomerShippingAddress email={props.customer.email}*/}
                {/*                                toggleAddShippingModal={toggleAddShippingModal}*/}
                {/*                                addShippingModalOpen={addShippingModalOpen}*/}
                {/*                                toggleEditShippingModal={toggleEditShippingModal}*/}
                {/*                                editShippingModalOpen={editShippingModalOpen}*/}
                {/*                                editingShippingAddress={editingShippingAddress}/>*/}

                <EditCustomerShippingAddress isOpen={editShippingModalOpen}
                                             toggle={() => setEditShippingModalOpen(false)}
                                             email={props.customer.email}
                                             shippingAddress={editingShippingAddress}/>


                <AddCustomerShippingAddress isOpen={addShippingModalOpen}
                                            toggle={() => setAddShippingModalOpen(false)}
                                            email={props.customer.email}/>


            </td>
        </>
    );
}

export default ActionButton;